import { refreshCourseSchedules, mergeVerifiedCourseSchedules } from "@/utils/refresh-course-schedules";
import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useReducer,
  useRef,
} from "react";
import { useRouter } from "next/router";
import { CognitoUser } from "amazon-cognito-identity-js";
import { Auth, Hub } from "aws-amplify";
import * as Sentry from "@sentry/nextjs";
import ImpersonationBanner from "@/components/account/impersonation-banner";
import { IMPERSONATION_TOKEN, IMPERSONATE_SESSION_PATH } from "@/lib/impersonation";
import { resolveOnlineBasketPricePence } from "@/lib/online-basket-checkout";
import { withResolvedLoyaltyPoints } from "@/lib/loyalty-points-balance";
import { capitalize, getPrices } from "@/pages/enroll";
import { createClient } from "@/utils/api";
import { captureFirstTouchAttribution } from "@/utils/client-attribution";
import { getClientByAuthId, setSignedIn } from "@/queries/clients";

export interface User extends CognitoUser {
  attributes: {
    [key: string]: any;
  };
}

interface State {
  loading: boolean;
  onlineBasket: boolean;
  onsiteCpdPackageCheckout: boolean;
  careerSponsorshipCheckout: boolean;
  earlyBirdCheckout: boolean;
  user?: User;
  type?: string;
  claims?: { [key: string]: string };
  token?: string;
  profile?: any;
  selectedCourses: any[];
  userData: any;
  sourceParams?: { [key: string]: string };
  impersonating?: boolean;
}

interface SessionContextValue extends State {
  onlineCourses: any[];
  addOnlineCourses: (course: any, data: any) => void;
  updateOnlineCourse: (courseUrl: string, updates: Record<string, unknown>) => void;
  removeOnlineCourses: (courseId: string) => void;
  signIn: ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => Promise<any>;
  signUp: ({
    email,
    password,
    first_name,
    last_name,
    phone,
  }: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone: string;
  }) => Promise<any>;
  logout: () => Promise<void>;
  setContext: React.Dispatch<React.SetStateAction<State>>;
  addCourse: (data: any) => void;
  replaceCourses: (courses: any[]) => void;
  removeCourse: (course: any) => void;
  empty: () => void;
  currentUser: (options?: CurrentUserOptions) => Promise<void>;
}

const initialState = {
  loading: true,
  user: undefined,
  type: undefined,
  onlineBasket: false,
  onsiteCpdPackageCheckout: false,
  careerSponsorshipCheckout: false,
  earlyBirdCheckout: false,
  token: undefined,
  claims: undefined,
  selectedCourses: [],
  userData: null,
  sourceParams: {},
  impersonating: false,
};

const getUser = async function (id: string, token: string): Promise<any> {
  const client = createClient(token);
  return client.request(getClientByAuthId, { id });
};

async function updateUser(id: string, token: string) {
  if (token === IMPERSONATION_TOKEN) {
    return null;
  }

  const client = createClient(token);
  return client.request(setSignedIn, { id });
}

async function loadImpersonationSession(): Promise<{
  claims: Record<string, string>;
  profile: any;
  cognitoId: string;
} | null> {
  try {
    const res = await fetch(IMPERSONATE_SESSION_PATH);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.active) return null;
    return {
      claims: data.claims,
      profile: data.profile,
      cognitoId: data.cognitoId,
    };
  } catch {
    return null;
  }
}

type CurrentUserOptions = {
  forceRefresh?: boolean;
  syncProfile?: boolean;
};

export const SessionContext = createContext(
  initialState as SessionContextValue,
);

export interface API {
  children: any;
}

export const Session = ({ children }: API) => {
  const [state, setState] = useState<State>(initialState);
  const router = useRouter();
  const signedInSyncRef = useRef<string | null>(null);

  const scheduleSelectionKey = state.selectedCourses.map(course => course.selectedEvent?.id ?? "").join(",");
  useEffect(() => {
    let active = true;
    let requestVersion = 0;
    const selected = state.selectedCourses;
    if (!selected.some(course => course.selectedEvent?.id)) return;
    const refresh = async () => {
      const version = ++requestVersion;
      const updated = await refreshCourseSchedules(selected);
      if (!active || version !== requestVersion) return;
      setState(current => {
        if (current.selectedCourses.map(course => course.selectedEvent?.id ?? "").join(",") !== scheduleSelectionKey) return current;
        const merged = mergeVerifiedCourseSchedules(current.selectedCourses, updated);
        sessionStorage.setItem("cc.courses", JSON.stringify(merged));
        return {...current, selectedCourses: merged};
      });
    };
    refresh();
    window.addEventListener("focus", refresh);
    return () => {active = false; window.removeEventListener("focus", refresh);};
  }, [scheduleSelectionKey, router.asPath]);

  const [onlineCourses, dispatch] = useReducer(courseReducer, []);

  const addOnlineCourses = (course, data) => {
    trackAdd(state.selectedCourses, {
      ...course,
      ...data,
      online: true,
    });

    dispatch({ type: ADD_COURSE, payload: course });
  };

  const removeOnlineCourses = (courseId) => {
    dispatch({ type: REMOVE_COURSE, payload: courseId });
  };

  const updateOnlineCourse = (courseUrl, updates) => {
    dispatch({
      type: UPDATE_COURSE,
      payload: { url: courseUrl, updates },
    });
  };

  const currentUser = useCallback(async (options: CurrentUserOptions = {}) => {
    const { forceRefresh = false, syncProfile = true } = options;

    try {
      const impersonation = await loadImpersonationSession();
      if (impersonation) {
        let selectedCourses = [] as any[];
        try {
          const courses = sessionStorage.getItem("cc.courses");
          selectedCourses = courses ? JSON.parse(courses) : [];
        } catch {
          selectedCourses = [];
        }
        setState((s) => ({
          ...s,
          loading: false,
          impersonating: true,
          user: {
            username: impersonation.cognitoId,
            attributes: {
              email: impersonation.profile?.email,
              given_name: impersonation.profile?.name,
              family_name: impersonation.profile?.surname,
              sub: impersonation.cognitoId,
            },
          } as unknown as User,
          claims: impersonation.claims,
          token: IMPERSONATION_TOKEN,
          userData: impersonation.profile,
          selectedCourses:
            selectedCourses.length > 0 ? selectedCourses : s.selectedCourses,
        }));
        return;
      }

      const data = await Auth.currentAuthenticatedUser(
        forceRefresh ? { bypassCache: true } : undefined,
      );
      const { idToken } = data.signInUserSession;
      const claims = idToken.payload;
      const token = idToken.jwtToken;

      if (data && data.attributes) {
        try {
          Sentry.setUser({
            id: data.attributes.sub,
            email: data.attributes.email,
          });
        } catch (err) {
          console.log(err);
        }

        try {
          fetch("/api/marketing/sync-user", { method: "POST" }).catch(
            () => undefined,
          );
        } catch (err) {
          console.log(err);
        }

        // @ts-ignore
        window.Intercom &&
          // @ts-ignore
          window.Intercom("boot", {
            app_id: "ha8vli9e",
            email: data.attributes.email,
            name: `${data.attributes.given_name} ${data.attributes.family_name}`,
            user_id: data.attributes.sub,
          });

      }

      const courses = sessionStorage.getItem("cc.courses");

      setState((s) => ({
        ...s,
        loading: false,
        impersonating: false,
        user: data as User,
        claims: {
          ...claims,
          ...JSON.parse(claims["https://hasura.io/jwt/claims"]),
        },
        token,
        selectedCourses: courses ? JSON.parse(courses) : [],
      }));

      const authLookupId =
        (typeof data.username === "string" && data.username.trim()) ||
        claims.sub;

      if (!syncProfile) {
        return;
      }

      const result = await getUser(authLookupId, token);
      const userData = withResolvedLoyaltyPoints(
        result.user && result.user[0],
      );

      if (
        userData &&
        (!userData.signed_in || !userData.set_password) &&
        signedInSyncRef.current !== authLookupId
      ) {
        signedInSyncRef.current = authLookupId;
        await updateUser(authLookupId, token);
      }

      setState((s) => ({
        ...s,
        userData,
      }));
    } catch {
      const courses = sessionStorage.getItem("cc.courses");
      setState((s) => ({
        ...s,
        loading: false,
        impersonating: false,
        selectedCourses: courses ? JSON.parse(courses) : [],
      }));
    }
  }, []);

  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;

  useEffect(() => {
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      const event = payload?.event;
      if (event === "signIn") {
        currentUserRef.current({ forceRefresh: true });
      }
      if (event === "tokenRefresh") {
        currentUserRef.current({ syncProfile: false });
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const courses = sessionStorage.getItem("cc.courses");
    const onsiteCpdPackage =
      sessionStorage.getItem("cc.onsiteCpdPackage") === "true";
    const careerSponsorship =
      sessionStorage.getItem("cc.careerSponsorshipCheckout") === "true";
    const earlyBirdCheckout =
      sessionStorage.getItem("cc.earlyBirdCheckout") === "true";

    if (courses) {
      setState((s) => ({
        ...s,
        selectedCourses: JSON.parse(courses),
        onsiteCpdPackageCheckout: onsiteCpdPackage,
        careerSponsorshipCheckout: careerSponsorship,
        earlyBirdCheckout,
      }));
      currentUser();
    } else {
      setState((s) => ({
        ...s,
        onsiteCpdPackageCheckout: onsiteCpdPackage,
        careerSponsorshipCheckout: careerSponsorship,
        earlyBirdCheckout,
      }));
      currentUser();
    }

    captureFirstTouchAttribution();

    if (window && window.location.search && window.location.search.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const params = Object.fromEntries(urlParams);
      try {
        // Merge with previously captured params instead of replacing them -
        // otherwise any later URL with an unrelated query string (filters,
        // ?session_id, cache-busters) wipes gclid/utm attribution.
        const existing = JSON.parse(localStorage.getItem("cc.p") || "{}");
        const merged = { ...existing, ...params };
        localStorage.setItem("cc.p", JSON.stringify(merged));
        const firstTouch = JSON.parse(localStorage.getItem("cc.ft") || "{}");
        setState((s) => ({ ...s, sourceParams: { ...firstTouch, ...merged } }));
      } catch (err) {
        console.log(err);
      }
    } else {
      try {
        const d = localStorage.getItem("cc.p");
        const e = JSON.parse(d);
        const firstTouch = JSON.parse(localStorage.getItem("cc.ft") || "{}");
        setState((s) => ({ ...s, sourceParams: { ...firstTouch, ...e } }));
      } catch (err) {
        console.log(err);
      }
    }

    const storedCourses = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (storedCourses) {
      dispatch({
        type: INITIALIZE_COURSES,
        payload: JSON.parse(storedCourses),
      });
    }

    // gtag && gtag('consent', 'update', {
    //   'ad_user_data': 'granted',
    //   'ad_personalization': 'granted',
    //   'ad_storage': 'granted',
    //   'analytics_storage': 'granted'
    // });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = useCallback(async () => {
    try {
      await Auth.signOut();
      Sentry.setUser(null);
      setState(initialState);
      router.replace("/");
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [router]);

  const signIn = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      try {
        const signInResult = await Auth.signIn({
          username: email,
          password,
        });
        currentUser();
        return signInResult;
      } catch (err) {
        Sentry.captureException(err);
        return false;
      }
    },
    [currentUser],
  );

  const signUp = useCallback(
    async ({
      email,
      password,
      first_name,
      last_name,
      phone,
    }: {
      email: string;
      password: string;
      first_name: string;
      last_name: string;
      phone: string;
    }) => {
      try {
        const { user } = await Auth.signUp({
          username: email,
          password,
          attributes: {
            email,
            given_name: first_name,
            family_name: last_name,
            phone_number: `+44${phone.replace(/^0+/, "").replace(/ /g, "")}`,
          },
        });
        const signIn = await Auth.signIn({
          username: email,
          password,
        });
        currentUser();
        return signIn;
      } catch (err) {
        Sentry.captureException(err);
        throw new Error(err.message || err);
      }
    },
    [currentUser],
  );

  const trackAdd = (selectedCourses, data) => {
    console.log("selectedCourses", selectedCourses);
    console.log("data", data);
    if (data.selectedEvent) {
      try {
        const t = getPrices({ courses: selectedCourses });
        window.gtag &&
          window.gtag("event", "add_to_cart", {
            currency: "GBP",
            value: t.total / 100,
            items: selectedCourses.map((x, i) => {
              return {
                item_id: x.package
                  ? x.event.id
                  : x.selectedEvent && x.selectedEvent.id
                    ? x.selectedEvent.id
                    : "No Event Id",
                item_name: x.fields.title,
                affiliation: "Cosmetic College",
                currency: "GBP",
                index: i,
                item_brand: "Cosmetic College",
                item_category: "Courses",
                item_category2: x.fields?.categories?.[0] || "Courses",
                item_list_id: x.fields?.categories?.[0] || "Courses",
                item_list_name: capitalize(
                  x.fields?.categories?.[0] || "courses",
                ),
                price: x.package
                  ? x.event.standardPrice / 100
                  : x.selectedEvent &&
                      (x.selectedEvent.price || x.selectedEvent.seat_price)
                    ? (x.selectedEvent.price || x.selectedEvent.seat_price) /
                      100
                    : 0,
                quantity: 1,
              };
            }),
          });

        (window as any).fbq &&
          (window as any).fbq("track", "AddToCart", {
            value: data.event.standardPrice / 100,
            currency: "GBP",
            content_name: data.fields.title,
            content_type: "product", // Required for Dynamic Product Ads
            content_ids: data.event.id,
          });
      } catch (err) {
        console.log(err);
        Sentry.captureException(err);
      }
    } else if (data.online) {
      const itemPrice = resolveOnlineBasketPricePence(data) / 100;

      try {
        const t = getPrices({ courses: selectedCourses });
        window.gtag &&
          window.gtag("event", "add_to_cart", {
            currency: "GBP",
            value: t.total / 100,
            items: selectedCourses.map((x, i) => {
              const iP = resolveOnlineBasketPricePence(x) / 100;

              return {
                item_id: x.sys?.id || x.event?.id,
                item_name: x.fields?.title,
                affiliation: "Cosmetic College",
                currency: "GBP",
                index: i,
                item_brand: "Cosmetic College",
                item_category: "Courses",
                item_category2: x.fields?.categories?.[0] || "Courses",
                item_list_id: x.fields?.categories?.[0] || "Courses",
                item_list_name: capitalize(x.fields?.categories?.[0] || "courses"),
                price: iP,
                quantity: 1,
              };
            }),
          });

        (window as any).fbq &&
          (window as any).fbq("track", "AddToCart", {
            value: itemPrice,
            currency: "GBP",
            content_name: data.fields.title,
            content_type: "product", // Required for Dynamic Product Ads
            content_ids: data.sys?.id || data.event?.id,
          });
      } catch (err) {
        console.log(err);
        Sentry.captureException(err);
      }
    }
  };

  const addCourse = useCallback(
    (data) => {
      const b = Array.isArray(data) ? data : [data];
      const selectedCourses = [...state.selectedCourses, ...b];

      setState((s) => ({
        ...s,
        selectedCourses,
      }));

      sessionStorage.setItem("cc.courses", JSON.stringify(selectedCourses));

      b.forEach((x) => {
        trackAdd(selectedCourses, x);
      });
    },
    [state],
  );

  const replaceCourses = useCallback((courses) => {
    const selectedCourses = Array.isArray(courses) ? courses : [];

    setState((s) => ({
      ...s,
      selectedCourses,
    }));

    sessionStorage.setItem("cc.courses", JSON.stringify(selectedCourses));

    selectedCourses.forEach((course) => {
      trackAdd(selectedCourses, course);
    });
  }, []);

  const removeCourse = useCallback(
    (course) => {
      const courses = [...state.selectedCourses];
      if (courses.length === 1) {
        setState((s) => ({
          ...s,
          selectedCourses: [],
        }));
        return sessionStorage.setItem("cc.courses", JSON.stringify([]));
      }

      const f = courses.findIndex((x) => x.event.id === course);
      if (f > -1) {
        courses.splice(f, 1);
        setState((s) => ({
          ...state,
          selectedCourses: courses,
        }));
        sessionStorage.setItem("cc.courses", JSON.stringify(courses));
      }
    },
    [state],
  );

  const empty = useCallback(() => {
    sessionStorage.removeItem("cc.onsiteCpdPackage");
    sessionStorage.removeItem("cc.careerSponsorshipCheckout");
    sessionStorage.removeItem("cc.careerSponsorshipCheckoutState");
    sessionStorage.removeItem("cc.earlyBirdCheckout");
    sessionStorage.setItem("cc.courses", JSON.stringify([]));
    setState((s) => ({
      ...s,
      selectedCourses: [],
      onsiteCpdPackageCheckout: false,
      careerSponsorshipCheckout: false,
      earlyBirdCheckout: false,
    }));
  }, []);

  const value: SessionContextValue = {
    ...state,
    onlineCourses,
    addOnlineCourses,
    updateOnlineCourse,
    removeOnlineCourses,
    logout,
    signIn,
    signUp,
    currentUser,
    setContext: setState,
    addCourse,
    replaceCourses,
    removeCourse,
    empty,
  };

  return (
    <SessionContext.Provider value={value}>
      {state.impersonating ? (
        <ImpersonationBanner
          email={state.userData?.email}
          cognitoId={
            (state.user as { username?: string } | undefined)?.username ||
            state.claims?.["x-hasura-user-id"]
          }
        />
      ) : null}
      {children}
    </SessionContext.Provider>
  );
};

export const SessionProvider = Session;

export const useSession = () => useContext(SessionContext);

const ADD_COURSE = "ADD_COURSE";
const REMOVE_COURSE = "REMOVE_COURSE";
const UPDATE_COURSE = "UPDATE_COURSE";
const INITIALIZE_COURSES = "INITIALIZE_COURSES";
const SESSION_STORAGE_KEY = "cc.online.courses";

function persistOnlineCourses(courses) {
  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(courses));
}

function courseReducer(state, action) {
  switch (action.type) {
    case ADD_COURSE: {
      const existingIndex = state.findIndex(
        (course) => course.fields.url === action.payload.fields.url,
      );
      const updatedCourses =
        existingIndex >= 0
          ? state.map((course, index) =>
              index === existingIndex ? action.payload : course,
            )
          : [...state, action.payload];
      persistOnlineCourses(updatedCourses);
      return updatedCourses;
    }

    case UPDATE_COURSE: {
      const updatedCourses = state.map((course) => {
        if (course.fields.url !== action.payload.url) {
          return course;
        }

        return {
          ...course,
          fields: {
            ...course.fields,
            ...action.payload.updates,
          },
        };
      });
      persistOnlineCourses(updatedCourses);
      return updatedCourses;
    }

    case REMOVE_COURSE: {
      const updatedCourses = state.filter(
        (course) => course.fields.url !== action.payload,
      );
      persistOnlineCourses(updatedCourses);
      return updatedCourses;
    }

    case INITIALIZE_COURSES: {
      return action.payload;
    }

    default:
      return state;
  }
}
